// MindForge web — TS port of config.py's mode configuration.
//
// Single source of truth for all 7 game modes. iOS, Pygame, and web must
// agree on these values (unlock level, start/max difficulty, rounds per
// session). Any change here needs to land in Swift and Python too.

export interface ModeConfig {
  readonly modeId: string;
  readonly name: string;
  readonly description: string;
  readonly iconColor: string; // CSS color matching Tailwind theme tokens
  readonly unlockLevel: number;
  readonly startDifficulty: number;
  readonly maxDifficulty: number;
  readonly rounds: number;
}

const ROUNDS_PER_SESSION = 10;

export const MODE_CONFIGS: readonly ModeConfig[] = [
  {
    modeId: "digit_span",
    name: "Digit Span",
    description: "Remember sequences of digits",
    iconColor: "var(--color-cyan)",
    unlockLevel: 1,
    startDifficulty: 5,
    maxDifficulty: 15,
    rounds: ROUNDS_PER_SESSION,
  },
  {
    modeId: "spatial",
    name: "Spatial Recall",
    description: "Remember grid positions in order",
    iconColor: "var(--color-teal)",
    unlockLevel: 3,
    startDifficulty: 3,
    maxDifficulty: 12,
    rounds: ROUNDS_PER_SESSION,
  },
  {
    modeId: "dual_task",
    name: "Dual Task",
    description: "Letters + math simultaneously",
    iconColor: "var(--color-green)",
    unlockLevel: 5,
    startDifficulty: 3,
    maxDifficulty: 12,
    rounds: ROUNDS_PER_SESSION,
  },
  {
    modeId: "nback",
    name: "N-Back",
    description: "Update and track letter sequences",
    iconColor: "var(--color-pink)",
    unlockLevel: 7,
    startDifficulty: 1,
    maxDifficulty: 8,
    rounds: ROUNDS_PER_SESSION,
  },
  {
    modeId: "go_nogo",
    name: "Go / No-Go",
    description: "React fast, suppress impulse",
    iconColor: "var(--color-red)",
    unlockLevel: 10,
    startDifficulty: 1,
    maxDifficulty: 10,
    rounds: 3,
  },
  {
    modeId: "rule_switch",
    name: "Rule Switch",
    description: "Adapt when the rules change",
    iconColor: "var(--color-amber)",
    unlockLevel: 16,
    startDifficulty: 1,
    maxDifficulty: 8,
    rounds: 30,
  },
  {
    modeId: "chunking",
    name: "Chunking Challenge",
    description: "Master memory grouping",
    iconColor: "var(--color-purple)",
    unlockLevel: 22,
    startDifficulty: 1,
    maxDifficulty: 10,
    rounds: ROUNDS_PER_SESSION,
  },
];

// Derived lookups built from MODE_CONFIGS.
export const MODE_ORDER: readonly string[] = MODE_CONFIGS.map((m) => m.modeId);

export const MODE_UNLOCKS: Readonly<Record<string, number>> = Object.fromEntries(
  MODE_CONFIGS.map((m) => [m.modeId, m.unlockLevel]),
);

export const MODE_START_DIFFICULTY: Readonly<Record<string, number>> =
  Object.fromEntries(MODE_CONFIGS.map((m) => [m.modeId, m.startDifficulty]));

export const MODE_MAX_DIFFICULTY: Readonly<Record<string, number>> =
  Object.fromEntries(MODE_CONFIGS.map((m) => [m.modeId, m.maxDifficulty]));

export const MODE_ROUNDS: Readonly<Record<string, number>> = Object.fromEntries(
  MODE_CONFIGS.map((m) => [m.modeId, m.rounds]),
);

export const MODE_BY_ID: Readonly<Record<string, ModeConfig>> =
  Object.fromEntries(MODE_CONFIGS.map((m) => [m.modeId, m]));

// Per-mode feedback-pause duration (ms) shown between rounds. Mirrors each
// desktop mode's `feedback_duration` override (and modeFeedbackDuration on
// iOS) so all three platforms pace identically: Rule Switch is a snappy
// 0.5s between its 30 rapid sorts; N-Back / Go-No-Go linger at 1.8s on
// their summaries. Modes not listed use the 1.2s default.
export const MODE_FEEDBACK_DURATION_MS: Readonly<Record<string, number>> = {
  digit_span: 1200,
  spatial: 1500,
  dual_task: 1500,
  nback: 1800,
  go_nogo: 1800,
  rule_switch: 500,
  chunking: 1500,
};

// Per-mode pre-trial "get ready" countdown (ms). A shrinking ring shown before
// a new trial's stimulus so it never appears abruptly. Mirrors COUNTDOWN_DURATION
// on desktop/iOS. 0 = no countdown: nback / go_nogo already run their own
// per-block "Get Ready" so the shared one is disabled to avoid doubling it.
export const MODE_COUNTDOWN_MS: Readonly<Record<string, number>> = {
  digit_span: 900,
  spatial: 900,
  dual_task: 900,
  chunking: 900,
  rule_switch: 900,
  nback: 0,
  go_nogo: 0,
};

// Modes whose countdown plays only before the FIRST trial of a session.
// rule_switch is ~30 rapid card-sorts; a per-sort countdown would wreck its
// pacing and reduce the switch-cost it trains, so it gets one get-ready at the
// start only.
export const COUNTDOWN_FIRST_TRIAL_ONLY: ReadonlySet<string> = new Set([
  "rule_switch",
]);

// Highest unlock level — used by the cheat code to target enough XP to open
// every mode at once.
export const MAX_UNLOCK_LEVEL: number = Math.max(
  ...MODE_CONFIGS.map((m) => m.unlockLevel),
);

/**
 * Beta-only override: when true, the main menu treats every mode as unlocked
 * regardless of the player's level. Lets testers try the full mode set
 * immediately without having to grind XP.
 *
 * Applied ONLY in the menu UI layer (see MainMenu.tsx) so the underlying
 * PlayerState.isUnlocked logic stays identical to iOS and Python — tests
 * keep passing, and the iOS app remains the only source of truth for the
 * shipped unlock economy.
 *
 * Flip to false to test the real unlock flow.
 */
export const BETA_UNLOCK_ALL = true;
